import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from "vue";
import { useEventStore, CalendarEvent, CalendarEventFilter } from "@/ts/stores/event";
import { Spinner } from "@/ts/components/elements";
import { FormInput, FormCombobox } from "@/ts/components/form";
import { EventCard, EventArchivedCard, EventRowCard } from "@/ts/components/events";
import { useDebounceFn } from "@vueuse/core";
import { parse, format } from "fecha";

interface Props {
    view?: "list" | "grid";
    full?: boolean;
    archived?: boolean;
    appName: string;
    backgroundFigures?: boolean;
    backgroundFiguresColor?: string;
}

interface EventFilter {
    categoryId: number | null;
}

/**
 * Use functions
 */

export default /*#__PURE__*/_defineComponent({
  props: {
    view: { type: String, required: false },
    full: { type: Boolean, required: false },
    archived: { type: Boolean, required: false },
    appName: { type: String, required: true },
    backgroundFigures: { type: Boolean, required: false },
    backgroundFiguresColor: { type: String, required: false }
  },
  setup(__props: any, { expose }) {
  expose();

const props = __props as {
    view?: "list" | "grid";
    full?: boolean;
    archived?: boolean;
    appName: string;
    backgroundFigures?: boolean;
    backgroundFiguresColor?: string;
}

var slugify = require("slugify");

/**
 * Interfaces
 */
const eventStore = useEventStore();

/**
 * Private variables
 */

const search = ref();
const date = ref();
const page = ref(1);
const per_page = EVENTS_PER_PAGE;
const end_reached = ref(false);
const loading = ref(true);
const debounceWaiting = ref(false);
const filter = ref<EventFilter>({
    categoryId: null,
});

/**
 * Get query filter
 */

const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop: string) => searchParams.get(prop),
});

//@ts-ignore
let category = params.categorie;

if (props.appName === "PLUG-IN") {
    category = "Plug-in";
}

/**
 * Retrieve API data
 */
const promises = [];
promises.push(eventStore.indexCategories(props.archived ? props.archived : false));
promises.push(eventStore.indexMonths(props.archived ? props.archived : false));
Promise.all(promises).then(() => {
    if (category) {
        const matchingCategory = eventStore.categories.find((c) => slugify(c.name) === category);
        if (matchingCategory) {
            filter.value.categoryId = matchingCategory.id;
        }
    }
    eventStore
        .indexEvents({
            ...(props.archived && { archived: true }),
            ...(filter.value.categoryId !== null && { categories: [filter.value.categoryId] }),
        })
        .then(() => {
            loading.value = false;
        });
});

/**
 * Computed variables
 */
const events = computed<CalendarEvent[]>(() => {
    const events = eventStore.all;
    return props.full ? events : events.slice(0, 5);
});

const canLoadMore = computed<boolean | undefined>(() => {
    return props.full && !loading.value && events.value.length > 0 && events.value.length % per_page === 0 && !end_reached.value;
});

const availableMonths = computed<{ value: string; label: string }[]>(() => {
    return eventStore.months.map((month: string) => {
        //const dateObj = parse(month, "YYYY-MM");
        const dateObj = parse(month, "YYYY-MM");
        return { value: month, label: format(dateObj!, "MMMM YYYY") };
    });
});

/**
 * Functions
 */
function toggleFilter(id: number) {
    if (filter.value.categoryId === id) {
        filter.value.categoryId = null;
    } else {
        filter.value.categoryId = id;
    }

    const url = new URL(window.location.href);

    if (filter.value.categoryId) {
        const matchingCategory = eventStore.categories.find((c) => c.id === filter.value.categoryId);
        if (matchingCategory) {
            url.searchParams.set("categorie", slugify(matchingCategory.name));
        }
    } else {
        url.searchParams.delete("categorie");
    }

    window.history.replaceState(null, "", url);

    loadMore(false);
}

function resetPagination() {
    end_reached.value = false;
    page.value = 1;
}

function loadMore(next_page: boolean = true) {
    debounceWaiting.value = true;
    loadMoreDebounced(next_page);
}

const loadMoreDebounced = useDebounceFn((next_page: boolean = true) => {
    debounceWaiting.value = false;

    if (next_page) {
        page.value++;
    } else {
        resetPagination();
    }

    loading.value = true;

    const eventFilter: CalendarEventFilter = {
        ...(filter.value.categoryId !== null && { categories: [filter.value.categoryId] }),
        ...(search.value && search.value.inputValue && search.value.inputValue !== "" && { search: search.value.inputValue }),
        ...(date.value && date.value.value && date.value.value.length > 0 && { dates: date.value.value }),
        ...(props.archived && { archived: true }),
        page: page.value,
    };

    const oldLength = eventStore.all.length;
    eventStore.indexEvents(eventFilter).then(() => {
        const newLength = eventStore.all.length;
        end_reached.value = oldLength === newLength;
        loading.value = false;
    });
}, 1000);

const __returned__ = { slugify, eventStore, props, search, date, page, per_page, end_reached, loading, debounceWaiting, filter, params, category, promises, events, canLoadMore, availableMonths, toggleFilter, resetPagination, loadMore, loadMoreDebounced, Spinner, FormInput, FormCombobox, EventCard, EventArchivedCard, EventRowCard }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})